import React from 'react';
import { FaTimes, FaCrown } from 'react-icons/fa';

/**
 * Upgrade Modal Component
 * Prompts users to upgrade their subscription
 * Follows Single Responsibility Principle - handles only upgrade prompts
 * 
 * @param {boolean} isOpen - Modal visibility state
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onUpgrade - Callback for upgrade action
 * @param {string} requiredTier - Required subscription tier
 * @param {string} featureName - Name of the locked feature
 * @param {Array} benefits - List of benefits for upgrading
 */
const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  onUpgrade, 
  requiredTier = 'Premium',
  featureName = 'this feature',
  benefits = []
}) => {
  if (!isOpen) return null;

  const defaultBenefits = benefits.length > 0 ? benefits : [
    'Unlimited AI-generated quizzes',
    'Unlimited doubt solving',
    'Advanced study planner',
    'Mock test generator',
    'Priority support',
    'Detailed analytics',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <FaTimes className="text-xl" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <FaCrown className="text-3xl" />
            <h2 className="text-2xl font-bold">Upgrade Required</h2>
          </div>
          <p className="text-purple-100">
            Unlock {featureName} with {requiredTier}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            To access {featureName}, upgrade to {requiredTier} and enjoy all premium features:
          </p>

          {/* Benefits List */}
          <div className="space-y-3 mb-8">
            {defaultBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
                <span className="text-gray-700 text-sm">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Maybe Later
            </button>
            <button
              onClick={() => {
                onUpgrade();
                onClose();
              }}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg transition-all duration-200 transform active:scale-95"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
